/**
 * Main chart + axis band background. Chronos assumes a dark surface (tick/grid colors);
 * do not use MUI `theme.palette.background.default` without a ThemeProvider — it stays #fff.
 */
export const CHRONOS_CANVAS_BG = "#1e1e24";

/** Chronofield terracotta / brown for event labels, connectors, dots. */
export const CHRONOS_EVENT_COLOR = "#b86b52";
export const CHRONOS_EVENT_BORDER = "#8d4d38";

/** Truncate names to this length (two lines × {@link TITLE_WRAP_CHARS}, ellipsis). */
export const EVENT_LABEL_MAX_CHARS = 120;

/** Target width (chars) for each title line; breaks on spaces (whole words). */
export const TITLE_WRAP_CHARS = 50;

/** Gantt row: two title lines + date line + padding (uniform row height). */
export const ROW_HEIGHT = 64;

/** Non-era period time-span bar (rect) height inside {@link ROW_HEIGHT}. */
export const PERIOD_BAR_HEIGHT = 34;

/** Minimum horizontal gap between item bounding boxes (px). */
export const GANTT_PACK_GAP_PX = 24;

/** Space reserved for dot + gap before label column (px). */
export const DOT_COLUMN_PX = 12;

/** Vertical band for era title above row content (px); fits {@link ERA_TITLE_FONT_PX}. */
export const ERA_LABEL_BAND = 24;

/** Era name in SVG (larger than event/period title). */
export const ERA_TITLE_FONT_PX = 16;

/** Single-line label box sizing (legacy / estimate). */
export const EVENT_LABEL_FONT_PX = 13;
export const EVENT_LABEL_PAD_X = 10;
export const EVENT_LABEL_PAD_Y = 6;
export const EVENT_LABEL_BORDER = 1;
export const EVENT_LABEL_HEIGHT =
  EVENT_LABEL_FONT_PX + 2 * EVENT_LABEL_PAD_Y + 2 * EVENT_LABEL_BORDER;

/** Horizontal extent cap for layout estimates (px); must fit ~60 title chars + long date ranges. */
export const EVENT_LABEL_MAX_WIDTH = 520;

/** Rough text width for sticky era label placement (matches truncated name length). */
export function estimateEraTitleWidthPx(text) {
  const t = String(text || "").trim();
  if (!t.length) return 12;
  const charW = ERA_TITLE_FONT_PX * 0.52;
  return Math.min(
    EVENT_LABEL_MAX_WIDTH,
    Math.ceil(t.length * charW) + 12
  );
}

/** Gap between stacked event rows (legacy; Gantt uses fixed ROW_HEIGHT). */
export const EVENT_STACK_GAP = 0;

export const DOT_RADIUS = 4;

/** Lane padding inside band. */
export const LANE_TOP_PAD = 6;
export const LANE_BOTTOM_PAD = 4;

/** Time axis + tick labels (top of chart). */
export const AXIS_BAND_HEIGHT = 56;

/** Minimum lane band height when empty (one row). */
export const LANE_MIN_HEIGHT = ROW_HEIGHT + LANE_TOP_PAD + LANE_BOTTOM_PAD;

/** Default zoom targets at most this many stacked Gantt rows per lane. */
export const MAX_STACK_TARGET = 7;

/** Default view domain: never wider than this (IRL years). */
export const DEFAULT_ZOOM_MAX_SPAN_YEARS = 500;

/** Smallest span (ms) tried when searching for a zoom that satisfies MAX_STACK_TARGET. */
export const DEFAULT_ZOOM_MIN_SPAN_MS = 864e5; // 1 day

/** Axis tick label color (readable on dark canvas; matches axis line tone). */
export const AXIS_TICK_LABEL_COLOR = "rgba(215, 215, 225, 0.92)";

/** Period row fill (opaque terracotta; eras stay lighter — see {@link ERA_LANE_FILL}). */
export const PERIOD_FILL = "rgb(184, 107, 82)";
export const PERIOD_FILL_STROKE = "rgb(115, 62, 48)";

/** Era full-lane background (more transparent; blends when overlapping). */
export const ERA_LANE_FILL = "rgba(184, 107, 82, 0.22)";

/** Era lane outline (same hue as era fill, darker for contrast). */
export const ERA_LANE_STROKE = "rgba(92, 50, 38, 0.9)";
/** Inner split line between contiguous eras in the same lane. */
export const ERA_LANE_DIVIDER = "rgba(120, 68, 51, 0.95)";

/** Dark halo behind era title glyphs (stroke drawn under fill). */
export const ERA_TITLE_HALO_STROKE = "rgba(22, 22, 28, 0.92)";
export const ERA_TITLE_HALO_STROKE_WIDTH = 2.5;

/** Grid line styling. */
export const GRID_LINE_COLOR = "rgba(200, 200, 210, 0.22)";
export const GRID_LINE_MAJOR = "rgba(200, 200, 210, 0.35)";
export const AXIS_LINE_COLOR = "rgba(210, 210, 220, 0.55)";

/** Lane sidebar width (labels + edit controls). */
export const LANE_SIDEBAR_WIDTH = 168;

/** Lane separator stroke (horizontal lines between lanes). */
export const LANE_SEPARATOR_COLOR = "rgba(200, 200, 210, 0.25)";
